import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Info } from "lucide-react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";

import { useAppSelector } from "../../../../../Store/Hooks";
import { isRole, ROLES } from "../../../../../Utils/Constants/RoleConstants";
import {
  getFromLocalStorage,
  STORAGE_KEYS,
} from "../../../../../Utils/Helper/LocalStorage";

export interface KpiDataUnavailableBannerProps {
  className?: string;
}

/**
 * Info tip for accounts lacking KPIs data (upload / contact program leader).
 * Renders tooltip via portal so sidebar overflow does not clip it.
 */
const KpiDataUnavailableBanner: React.FC<KpiDataUnavailableBannerProps> = ({
  className = "",
}) => {
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { user } = useAppSelector(state => state.authentication);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);

  const canConfigureKpis = useMemo(() => {
    const userRole = user?.user?.role || loggedInUser?.role;
    const executiveOps =
      loggedInUser?.executiveOps || user?.user?.executive_ops || false;
    return (
      isRole(userRole, ROLES.VENDOR_ADMIN) ||
      isRole(userRole, ROLES.C_LEVEL) ||
      isRole(userRole, ROLES.EXECUTIVE_SPONSOR_LEAD) ||
      Boolean(executiveOps)
    );
  }, [loggedInUser, user]);

  const [infoOpen, setInfoOpen] = useState(false);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const openInfo = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setInfoOpen(true);
  }, []);

  const scheduleCloseInfo = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setInfoOpen(false);
      closeTimerRef.current = null;
    }, 200);
  }, []);

  const updateTooltipPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const tooltipEl = tooltipRef.current;
    const tipWidth = tooltipEl?.offsetWidth ?? 280;
    const tipHeight = tooltipEl?.offsetHeight ?? 48;
    const gap = 6;
    const padding = 8;

    // Center horizontally under the info icon
    let left = rect.left + rect.width / 2 - tipWidth / 2;
    let top = rect.bottom + gap;

    // Keep within viewport horizontally
    left = Math.min(left, window.innerWidth - tipWidth - padding);
    left = Math.max(padding, left);

    // Flip above if clipped at bottom
    if (top + tipHeight > window.innerHeight - padding) {
      top = rect.top - tipHeight - gap;
    }
    top = Math.max(padding, top);

    setTooltipPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!infoOpen) return;
    updateTooltipPosition();
    const frame = requestAnimationFrame(() => updateTooltipPosition());
    return () => cancelAnimationFrame(frame);
  }, [infoOpen, updateTooltipPosition, canConfigureKpis]);

  useEffect(() => {
    if (!infoOpen) return;
    const onReposition = () => updateTooltipPosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [infoOpen, updateTooltipPosition]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const executiveStaffPath = orgSlug
    ? `/${orgSlug}/executive-staff?role=${ROLES.EXECUTIVE_SPONSOR_LEAD}`
    : `/executive-staff?role=${ROLES.EXECUTIVE_SPONSOR_LEAD}`;
  const kpiDataUploadPath = orgSlug
    ? `/${orgSlug}/ops?tab=data-upload`
    : "/ops?tab=data-upload";

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="fixed z-[10000] max-w-[min(320px,calc(100vw-16px))] rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 shadow-md"
      style={{ top: tooltipPos.top, left: tooltipPos.left }}
      onMouseEnter={openInfo}
      onMouseLeave={scheduleCloseInfo}
    >
      {canConfigureKpis ? (
        <>
          You can upload KPIs data for these accounts. Go to{" "}
          <Link
            to={kpiDataUploadPath}
            className="font-medium text-purple-600 hover:underline"
            onClick={() => setInfoOpen(false)}
          >
            Upload KPI data
          </Link>
          .
        </>
      ) : (
        <>
          Contact your program leader to request KPIs data (see{" "}
          <Link
            to={executiveStaffPath}
            className="font-medium text-purple-600 hover:underline"
            onClick={() => setInfoOpen(false)}
          >
            Executive Staff
          </Link>
          ).
        </>
      )}
    </div>
  );

  return (
    <div
      ref={triggerRef}
      className={`relative inline-flex shrink-0 items-center ${className}`}
      onMouseEnter={openInfo}
      onMouseLeave={scheduleCloseInfo}
      onClick={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
    >
      <button
        type="button"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-orange-500 hover:bg-orange-50 hover:text-orange-500 focus:text-orange-500"
        aria-label="KPI data availability info"
        aria-expanded={infoOpen}
        onFocus={openInfo}
        onBlur={scheduleCloseInfo}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          if (infoOpen) {
            scheduleCloseInfo();
          } else {
            openInfo();
          }
        }}
      >
        <Info
          className="h-3.5 w-3.5 text-orange-500"
          strokeWidth={2}
          aria-hidden
        />
      </button>
      {infoOpen ? createPortal(tooltipContent, document.body) : null}
    </div>
  );
};

export default KpiDataUnavailableBanner;
